import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { warehouseApi, type WarehousePayload } from '@/api/warehouse';

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string | string[] } } }).response;
    const message = response?.data?.message;
    return Array.isArray(message) ? message.join('; ') : message || fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

/**
 * Get warehouse items (my items + office stock)
 */
export function useWarehouse() {
  return useQuery({
    queryKey: ['warehouse'],
    queryFn: () => warehouseApi.getGrouped(),
    staleTime: 60_000, // 1 minute
  });
}

/**
 * Create new warehouse item on office stock
 */
export function useCreateWarehouseItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: WarehousePayload) => warehouseApi.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouse'] });
      toast.success('Товар добавлен на склад');
    },
    onError: (error) => {
      toast.error(errorMessage(error, 'Ошибка добавления товара'));
    },
  });
}

/**
 * Take item for a client
 */
export function useTakeItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, clientId, clientName }: { id: string; clientId?: number; clientName?: string }) =>
      warehouseApi.takeForClient(id, { clientId, clientName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouse'] });
      toast.success('Товар взят');
    },
    onError: () => {
      toast.error('Ошибка при взятии товара');
    },
  });
}

/**
 * Take item for self (рабочая единица)
 */
export function useTakeForSelf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => warehouseApi.takeForSelf(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouse'] });
      toast.success('Товар взят для себя');
    },
    onError: () => {
      toast.error('Ошибка при взятии товара');
    },
  });
}

/**
 * Return item to warehouse
 */
export function useReturnItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => warehouseApi.returnToOffice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouse'] });
      toast.success('Товар возвращён на склад');
    },
    onError: () => {
      toast.error('Ошибка при возврате товара');
    },
  });
}
